import { Bundle, InstallmentPlanCode, InstallmentPlanPricing, Money, Product, ProductPricing } from 'src/services/dcs/bundle';

/**
 * Removes all keys from an object that contain undefined or null values
 * @param object The object to clean
 * @returns {object} The object with all keys that contain undefined or null values removed
 */
const cleanObject = (object: Record<string, any>): object => {
  const updatedObject = {};
  Object.keys(object).forEach(key => {
    const currentValue = object[key];
    const isObject = currentValue && typeof currentValue === 'object';
    const isDefinedValue = !isObject && currentValue !== undefined && currentValue !== null;
    if (isObject) {
      const nestedObj = cleanObject(currentValue);
      if (Object.keys(nestedObj).length) {
        updatedObject[key] = nestedObj;
      }
    } else if (isDefinedValue) {
      updatedObject[key] = currentValue;
    }
  });
  return updatedObject;
};

const getInstallmentPlanMap = (productPricing: ProductPricing[]) => {
  if (!productPricing) return {};

  return productPricing.reduce((map, pricing) => {
    if (pricing.installmentPlan.code !== InstallmentPlanCode.P) {
      map[pricing.installmentPlan.code] = pricing.price;
    }
    return map;
  }, {});
};

const toLocaleString = (num: number) => num.toLocaleString(undefined, { maximumFractionDigits: 2 });

const getPricingObject = (pricingObject: any[], msrp: Money | null) => {
  const pricing = pricingObject.find(pricing => pricing.installmentPlan.code === InstallmentPlanCode.P);
  if (!pricing) return {};

  const basePrice = pricing.price;
  const installmentPlans: InstallmentPlanPricing = getInstallmentPlanMap(pricingObject);
  return {
    msrp: msrp ? toLocaleString(msrp.value) : null,
    total: toLocaleString(basePrice.value),
    installmentPlans: {
      ip3: installmentPlans.IP3?.value,
      ip6: installmentPlans.IP6?.value,
      ip12: installmentPlans.IP12?.value,
    },
  };
};

const transformBundleForTemplate = (bundle?: Bundle) => {
  if (!bundle) return;

  const { msrp } = bundle;
  const bundlePricingObject = getPricingObject(bundle.productPricing, msrp);
  const { keyedInRate, inPersonRate } = bundle.softwarePlanPricing.transactionRates;
  const monthlyAdditionalPrice = bundle.softwarePlanPricing.monthlyPrice;

  return cleanObject({
    pricing: {
      softwarePlan: {
        monthlyPrice: toLocaleString(monthlyAdditionalPrice.value),
        additionalDevicePrice: toLocaleString(bundle.softwarePlanPricing.additionalDevicePrice.value),
        transactionRates: {
          keyedInRate: {
            percentage: keyedInRate.percentage,
            fixedAmount: toLocaleString(keyedInRate.fixedAmount.value),
          },
          inPersonRate: {
            percentage: inPersonRate.percentage,
            fixedAmount: toLocaleString(inPersonRate.fixedAmount.value),
          },
        },
      },
      ...bundlePricingObject,
    },
  });
};

const transformProductForTemplate = (product?: Product) => {
  if (!product) return;

  const { msrp } = product;
  const productPricingObject = getPricingObject(product.pricing, msrp);

  return cleanObject({
    pricing: {
      ...productPricingObject,
    },
  });
};

export {
  transformBundleForTemplate,
  transformProductForTemplate
};
